import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Quality.css";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import {
    Bars, Kpi, MockBanner, People, ReleasePill, ResultPill, SevChip, StagePill, fmtDate, fmtShort, hasPerm, pct,
} from "./qualityUi";

/**
 * One production release: its tickets, its regression run, and what escaped.
 *
 * The release gate is the point of this screen. A release in regression can be
 * validated only when every case has a result and every failed or blocked case
 * carries a waiver reason. The backend enforces the same rule; the banner and
 * the disabled button say why before anyone presses it.
 */
const TABS = [
    { key: "tickets", label: "Tickets" },
    { key: "run", label: "Regression run" },
    { key: "quality", label: "Quality" },
];

export default function ProductionReleaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [rel, setRel] = useState(null);
    const [err, setErr] = useState("");
    const [tab, setTab] = useState("tickets");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await qualityApi.getRelease(id);
            setRel(res.data);
            setErr("");
        } catch (e) {
            setErr(e.message || "Could not load this release.");
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const saveExecution = async (execId, patch) => {
        try {
            const res = await qualityApi.updateExecution(execId, patch);
            setRel(res.data);
        } catch (e) {
            showErrorToast(e.message || "Could not save the result.");
        }
    };

    const validate = async () => {
        setBusy(true);
        try {
            const res = await qualityApi.validateRelease(id);
            setRel(res.data);
            showSuccessToast(res.message || "Release validated.");
        } catch (e) {
            showErrorToast([e.message, ...(e.payload?.blockers || [])].join(" "));
        } finally {
            setBusy(false);
        }
    };

    if (err) return <div className="qa-page"><MockBanner /><div className="qa-empty">{err}</div></div>;
    if (!rel) return <div className="qa-page"><MockBanner /><div className="qa-empty">Loading…</div></div>;

    const s = rel.stats;
    const g = rel.gate;
    const open = rel.status === "in_regression";
    const share = (n) => (s.total ? (n / s.total) * 100 : 0);

    return (
        <div className="qa-page">
            <MockBanner />
            <div className="qa-head">
                <button className="qa-back" onClick={() => navigate("/quality/releases")} aria-label="Back">←</button>
                <h1 className="qa-mono">{rel.version}</h1>
                <ReleasePill value={rel.status} />
                <span className="qa-hint">Release date {fmtDate(rel.release_date)}</span>
                <span className="qa-sp" />
                {open && (
                    <>
                        <button className="qa-btn" onClick={() => navigate("/live-move")}>Add from Live Move</button>
                        {hasPerm("Manage Releases") && (
                            <button
                                className="qa-btn qa-btn--primary" disabled={!g.ok || busy} onClick={validate}
                                title={g.ok ? "" : "Finish the regression run first"}
                            >
                                Validate release
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="qa-meta" style={{ margin: "-6px 0 14px" }}>
                {rel.servers.map((x) => <span key={x.code} className="qa-chip">{x.label}: {x.version}</span>)}
            </div>

            {open && (g.ok ? (
                <div className="qa-banner qa-banner--ok">
                    <span className="qa-banner__tx">Every case has a result and every failure is waived. You can validate this release.</span>
                </div>
            ) : (
                <div className="qa-banner qa-banner--bad">
                    <span className="qa-banner__tx">
                        <b>Release gate closed.</b>{" "}
                        {g.not_run > 0 && `${g.not_run} case${g.not_run > 1 ? "s" : ""} not run. `}
                        {g.unwaived > 0 && `${g.unwaived} failed or blocked case${g.unwaived > 1 ? "s" : ""} without a waiver.`}
                    </span>
                    {tab !== "run" && <button className="qa-btn qa-btn--sm" onClick={() => setTab("run")}>Open regression run</button>}
                </div>
            ))}

            <div className="qa-kpis">
                <Kpi value={rel.tickets.length} label="Tickets" color="var(--accent)" fill={100} />
                <Kpi value={`${s.coverage}%`} label="Regression coverage" color={s.coverage === 100 ? "var(--qa-green)" : "var(--qa-amber)"} fill={s.coverage} />
                <Kpi value={s.pass} label="Passed" color="var(--qa-green)" fill={share(s.pass)} />
                <Kpi value={s.fail} label="Failed" color="var(--qa-red)" fill={share(s.fail)} />
                <Kpi value={s.blocked} label="Blocked" color="var(--qa-orange)" fill={share(s.blocked)} />
                <Kpi value={s.escaped} label="Escaped bugs" color="var(--qa-red)" fill={s.escaped * 25} />
                <Kpi value={pct(s.leakage)} label="Defect leakage" color="var(--qa-purple)" fill={s.leakage} />
            </div>

            <div className="qa-tabs" style={{ marginBottom: 14 }}>
                {TABS.map((t) => (
                    <button key={t.key} className={`qa-tab${tab === t.key ? " qa-tab--on" : ""}`} onClick={() => setTab(t.key)}>
                        {t.label}
                        {t.key === "run" && open && !g.ok && <span className="qa-tab__badge">{g.not_run + g.unwaived}</span>}
                    </button>
                ))}
            </div>

            {tab === "tickets" && <TicketsTab rel={rel} />}
            {tab === "run" && <RunTab rel={rel} editable={open && hasPerm("Run Regression")} onSave={saveExecution} />}
            {tab === "quality" && <QualityTab rel={rel} />}
        </div>
    );
}

function TicketsTab({ rel }) {
    return (
        <>
            <div className="qa-toolbar">
                <span className="qa-toolbar__count">People as they were when each ticket joined the release</span>
            </div>
            <div className="qa-card">
                <table className="qa-tbl">
                    <thead>
                        <tr>
                            <th>Ticket No</th><th>Description</th><th>Module</th><th>Sprint</th><th>Developer</th>
                            <th>QA</th><th>UAT</th><th>Approved by</th><th>Test cases</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rel.tickets.map((t) => (
                            <tr key={t.code}>
                                <td className="qa-code">{t.code}</td>
                                <td><div className="qa-trunc" style={{ maxWidth: 280 }}>{t.description}</div></td>
                                <td>{t.module}</td>
                                <td className="qa-nw qa-muted">{t.sprint}</td>
                                <td><People names={t.developers} /></td>
                                <td className="qa-nw">{t.qa}{t.peer_qa && <div className="qa-hint">Peer {t.peer_qa}</div>}</td>
                                <td className="qa-nw">{t.uat}</td>
                                <td className="qa-nw">{t.approver || <span className="qa-pill qa-pill--warn">Pending</span>}</td>
                                <td className="qa-nw qa-mono" style={{ fontSize: ".74rem" }}>
                                    {t.test_cases.length ? t.test_cases.join(", ") : <span className="qa-pill qa-pill--danger">None</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function RunTab({ rel, editable, onSave }) {
    return (
        <>
            <div className="qa-toolbar">
                <span className="qa-toolbar__count">Regression run · {rel.executions.length} cases</span>
                <span className="qa-hint">Regression cases for {rel.modules.join(", ")}, plus all critical cases</span>
            </div>
            <div className="qa-card">
                <table className="qa-tbl">
                    <thead>
                        <tr>
                            <th>Test case</th><th>Title</th><th>Module</th><th>Result</th><th>Tester</th>
                            <th>Date</th><th>Comment</th><th>Waiver reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rel.executions.map((x) => (
                            <ExecutionRow key={x.id} x={x} editable={editable} onSave={onSave} />
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function ExecutionRow({ x, editable, onSave }) {
    const needsWaiver = x.result === "fail" || x.result === "blocked";
    const [waiver, setWaiver] = useState(x.waiver_reason || "");
    const timer = useRef(null);
    useEffect(() => { setWaiver(x.waiver_reason || ""); }, [x.waiver_reason]);

    // Waivers save as the tester types, debounced, so the gate updates without
    // a separate save button per row.
    const onWaiver = (v) => {
        setWaiver(v);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => onSave(x.id, { waiver_reason: v }), 500);
    };
    useEffect(() => () => clearTimeout(timer.current), []);

    return (
        <tr>
            <td className="qa-code qa-mono">
                {x.test_case.code}{x.test_case.critical && <> <span className="qa-chip qa-chip--red">Critical</span></>}
            </td>
            <td>{x.test_case.title}</td>
            <td className="qa-muted">{x.test_case.module}</td>
            <td>
                {editable ? (
                    <select
                        id={`qa-exec-${x.id}`} className="qa-filter" value={x.result} aria-label={`Result for ${x.test_case.code}`}
                        onChange={(e) => onSave(x.id, { result: e.target.value })}
                    >
                        <option value="not_run">Not run</option>
                        <option value="pass">Pass</option>
                        <option value="fail">Fail</option>
                        <option value="blocked">Blocked</option>
                    </select>
                ) : <ResultPill value={x.result} />}
            </td>
            <td className="qa-nw">{x.tester || "—"}</td>
            <td className="qa-nw qa-muted">{fmtShort(x.executed_at)}</td>
            <td className="qa-hint">{x.comment}</td>
            <td>
                {needsWaiver ? (
                    editable ? (
                        <input
                            id={`qa-waive-${x.id}`} className="qa-input" style={{ width: 190 }} value={waiver}
                            placeholder="Required to validate" onChange={(e) => onWaiver(e.target.value)}
                        />
                    ) : x.waiver_reason || <span className="qa-pill qa-pill--danger">No waiver</span>
                ) : <span className="qa-faint">—</span>}
            </td>
        </tr>
    );
}

function QualityTab({ rel }) {
    const navigate = useNavigate();
    const s = rel.stats;
    const bySev = ["P0", "P1", "P2", "P3"].map((k) => ({
        label: k, value: rel.escaped_bugs.filter((b) => b.severity === k).length, color: `var(--qa-${k.toLowerCase()})`,
    }));
    return (
        <div className="qa-dgrid">
            <div className="qa-box">
                <div className="qa-sec">
                    <h4>Escaped bugs</h4>
                    {rel.escaped_bugs.length ? (
                        <div className="qa-card qa-card--solo">
                            <table className="qa-tbl">
                                <tbody>
                                    {rel.escaped_bugs.map((b) => (
                                        <tr key={b.id} className="qa-click" onClick={() => navigate(`/quality/bugs/${b.id}`)}>
                                            <td className="qa-code">{b.code}</td>
                                            <td><div className="qa-trunc" style={{ maxWidth: 240 }}>{b.title}</div></td>
                                            <td><SevChip value={b.severity} /></td>
                                            <td><StagePill value={b.stage} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : <p className="qa-hint">No production bugs traced to this release.</p>}
                    <p className="qa-hint" style={{ margin: "12px 0 0" }}>
                        Defect leakage = {s.escaped} production ÷ ({s.escaped} production + {s.dev_bugs} found by QA) ={" "}
                        <b style={{ color: "var(--text-primary)" }}>{pct(s.leakage)}</b>
                    </p>
                </div>
            </div>
            <div className="qa-box">
                <div className="qa-sec">
                    <h4>By severity</h4>
                    <Bars rows={bySev} labelWidth={50} />
                </div>
            </div>
        </div>
    );
}
